import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  usePortfolios,
  useCreatePortfolio,
  useUpdatePortfolio,
  useDeletePortfolio,
} from "../../hooks/queries/usePortfolios";
import LogoLoader from "../common/LogoLoader";
import { CreatePortfolioRequest, Portfolio } from "../../services/portfolioApi";

const PortfolioListView: React.FC = () => {
  const navigate = useNavigate();

  // TanStack Query hooks
  const portfoliosQuery = usePortfolios();
  const createMutation = useCreatePortfolio();
  const updateMutation = useUpdatePortfolio();
  const deleteMutation = useDeletePortfolio();

  const portfolios = portfoliosQuery.data ?? [];
  const loading = portfoliosQuery.isLoading;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [newPortfolioDescription, setNewPortfolioDescription] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(
    null,
  );
  const [editPortfolioName, setEditPortfolioName] = useState("");
  const [editPortfolioDescription, setEditPortfolioDescription] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<Portfolio | null>(
    null,
  );

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) return;

    try {
      const data: CreatePortfolioRequest = {
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim() || undefined,
      };

      const newPortfolio = await createMutation.mutateAsync(data);
      setCreateDialogOpen(false);
      setNewPortfolioName("");
      setNewPortfolioDescription("");

      // Navigate to the new portfolio
      navigate(`/portfolio?portfolio_id=${newPortfolio.id}`);
    } catch (error) {
      return;
    }
  };

  const handleDeletePortfolio = (portfolio: Portfolio) => {
    setPortfolioToDelete(portfolio);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!portfolioToDelete) return;

    try {
      await deleteMutation.mutateAsync(portfolioToDelete.id);
      setDeleteDialogOpen(false);
      setPortfolioToDelete(null);
    } catch (error) {
      return;
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPortfolioToDelete(null);
  };

  const handleEditPortfolio = (portfolio: Portfolio) => {
    setEditingPortfolio(portfolio);
    setEditPortfolioName(portfolio.name);
    setEditPortfolioDescription(portfolio.description || "");
    setEditDialogOpen(true);
  };

  const handleUpdatePortfolio = async () => {
    if (!editingPortfolio || !editPortfolioName.trim()) return;

    try {
      await updateMutation.mutateAsync({
        id: editingPortfolio.id,
        data: {
          name: editPortfolioName.trim(),
          description: editPortfolioDescription.trim() || undefined,
        },
      });

      setEditDialogOpen(false);
      setEditingPortfolio(null);
      setEditPortfolioName("");
      setEditPortfolioDescription("");
    } catch (error) {
      return;
    }
  };

  const getTotalItemCount = (portfolio: Portfolio): number => {
    return Object.values(portfolio.entity_counts).reduce(
      (sum, count) => sum + count,
      0,
    );
  };

  const getPermissionColor = (permission: string | null) => {
    switch (permission) {
      case "OWNER":
        return "primary";
      case "EDITOR":
        return "secondary";
      case "VIEWER":
        return "default";
      default:
        return "default";
    }
  };

  const columns = useMemo<MRT_ColumnDef<Portfolio>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Portfolio Name",
        size: 250,
        Cell: ({ cell, row }) => (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ cursor: "pointer", color: "primary.main" }}
              onClick={() =>
                navigate(`/portfolio?portfolio_id=${row.original.id}`)
              }
            >
              {cell.getValue<string>()}
            </Typography>
            {/* {row.original.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {row.original.description}
            </Typography>
          )} */}
          </Box>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 300,
        Cell: ({ cell }) => {
          const description = (cell.getValue() as string) || "No description";
          const truncatedDescription =
            description.length > 150
              ? `${description.substring(0, 150)}...`
              : description;

          return (
            <Typography
              variant="body2"
              color="text.secondary"
              title={description}
            >
              {truncatedDescription}
            </Typography>
          );
        },
      },
      {
        id: "total_items",
        accessorKey: "entity_counts",
        header: "Total Items",
        size: 120,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {getTotalItemCount(row.original)}
          </Typography>
        ),
      },
      {
        id: "entity_breakdown",
        accessorKey: "entity_counts",
        header: "Entity Breakdown",
        size: 300,
        Cell: ({ row }) => {
          const counts = row.original.entity_counts;
          const entityTypes = [
            { key: "organization", label: "Orgs", color: "#1976d2" },
            { key: "facility", label: "Facilities", color: "#388e3c" },
            { key: "project", label: "Projects", color: "#f57c00" },
            { key: "source", label: "Sources", color: "#7b1fa2" },
            { key: "person", label: "People", color: "#d32f2f" },
            { key: "event", label: "Events", color: "#455a64" },
          ];

          return (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {entityTypes.map((type) => {
                const count = counts[type.key] || 0;
                if (count === 0) return null;
                return (
                  <Chip
                    key={type.key}
                    label={`${count} ${type.label}`}
                    size="small"
                    sx={{
                      backgroundColor: type.color,
                      color: "white",
                      fontWeight: "bold",
                      height: 20,
                      fontSize: "0.7rem",
                      "& .MuiChip-label": {
                        px: 0.75,
                      },
                    }}
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "user_permission",
        header: "Your Role",
        size: 120,
        Cell: ({ cell }) => {
          const permission = cell.getValue<string | null>();
          return permission ? (
            <Chip
              label={permission}
              size="small"
              color={getPermissionColor(permission) as any}
              variant="outlined"
            />
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "created_by_username",
        header: "Created By",
        size: 120,
      },
      {
        accessorKey: "created_at",
        header: "Created",
        size: 120,
        Cell: ({ cell }) =>
          new Date(cell.getValue<string>()).toLocaleDateString(),
      },
      {
        accessorKey: "updated_at",
        header: "Updated",
        size: 120,
        Cell: ({ cell }) =>
          new Date(cell.getValue<string>()).toLocaleDateString(),
      },
    ],
    [navigate],
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Portfolios</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Portfolio
        </Button>
      </Box>

      {/* Portfolio Table */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "400px",
            backgroundColor: "background.paper",
          }}
        >
          <LogoLoader size="panel" surface="paper" label="Loading the portfolios" />
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={portfolios}
          // state={{ isLoading: loading }}
          enableRowActions
          renderRowActions={({ row }) => (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="View portfolio">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/portfolio?portfolio_id=${row.original.id}`)
                  }
                >
                  <ViewIcon />
                </IconButton>
              </Tooltip>

              {(row.original.user_permission === "OWNER" ||
                row.original.user_permission === "EDITOR") && (
                <Tooltip title="Edit portfolio">
                  <IconButton
                    size="small"
                    onClick={() => handleEditPortfolio(row.original)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}

              {row.original.user_permission === "OWNER" && (
                <Tooltip title="Delete portfolio">
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePortfolio(row.original)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          muiTableBodyRowProps={{
            hover: true,
          }}
          muiTableProps={{
            sx: {
              "& .MuiTableHead-root": {
                backgroundColor: "#1e1e1e !important",
              },
              "& .MuiTableHead-root .MuiTableRow-root": {
                backgroundColor: "#1e1e1e !important",
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#1e1e1e !important",
              },
              "& .MuiTableBody-root": {
                backgroundColor: "#1e1e1e",
              },
              "& .MuiTableFooter-root": {
                backgroundColor: "#1e1e1e",
              },
            },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              backgroundColor: "#1e1e1e",
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              backgroundColor: "#1e1e1e",
            },
          }}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: "#1e1e1e",
            },
          }}
          initialState={{
            pagination: { pageSize: 25, pageIndex: 0 },
            sorting: [{ id: "updated_at", desc: true }],
          }}
        />
      )}
      {/* Create Portfolio Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Portfolio</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Portfolio Name"
            value={newPortfolioName}
            onChange={(e) => setNewPortfolioName(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={newPortfolioDescription}
            onChange={(e) => setNewPortfolioDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreatePortfolio}
            variant="contained"
            disabled={!newPortfolioName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Portfolio Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Portfolio</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Portfolio Name"
            value={editPortfolioName}
            onChange={(e) => setEditPortfolioName(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={editPortfolioDescription}
            onChange={(e) => setEditPortfolioDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdatePortfolio}
            variant="contained"
            disabled={!editPortfolioName.trim() || updateMutation.isPending}
            startIcon={
              updateMutation.isPending ? <CircularProgress size={16} /> : null
            }
          >
            {updateMutation.isPending ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Portfolio Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            Delete Portfolio
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This action will permanently delete the portfolio "
              {portfolioToDelete?.name}" and all its contents. This cannot be
              undone.
            </Typography>

            {portfolioToDelete && (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Portfolio details:
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                    borderRadius: 1,
                    border: "1px solid rgba(211, 47, 47, 0.3)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    {portfolioToDelete.name}
                  </Typography>
                  {portfolioToDelete.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {portfolioToDelete.description}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Total items: {getTotalItemCount(portfolioToDelete)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created:{" "}
                    {new Date(
                      portfolioToDelete.created_at,
                    ).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete Portfolio"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfolioListView;
